// Desktop-only sticky preview pane for the property wizard.
//
// Renders the draft as a card resembling the live property card the
// user will see in the listing grid, plus an AI hint banner. Hidden
// on mobile — phones don't have room for a side-by-side preview, and
// the design's mobile flow skips it.

import { Bed, Sparkles, Square } from "lucide-react";

import { AuthImg } from "@/components/auth-img";
import { cn } from "@/lib/utils";
import { wardLabel } from "@/features/property/utils/ward-options";
import {
  bedroomLabel,
  propertyTypeLabel,
} from "@/features/property/utils/property-enum-options";
import type { PropertyDraft } from "@/features/property/wizard/wizard-state";

interface PreviewPanelProps {
  draft: PropertyDraft;
  /**
   * URL of the image to render as the preview hero. In create mode the
   * caller passes the first {@link PendingImage}'s object URL; in edit
   * mode it can be a presigned URL from the property's existing images.
   * Falls back to a gradient placeholder when {@code undefined}.
   */
  heroImageUrl?: string | undefined;
}

export function PreviewPanel({ draft, heroImageUrl }: PreviewPanelProps) {
  const title = draft.buildingName || "Tên toà nhà sẽ hiển thị ở đây";
  const ward = draft.wardCode ? wardLabel(draft.wardCode) : "Khu vực";
  const floor = draft.floor != null ? `Tầng ${draft.floor}` : "Tầng --";
  const beds = draft.bedrooms != null ? `${bedroomLabel(draft.bedrooms)} PN` : "-- PN";
  const area = draft.area != null ? `${draft.area} m²` : "-- m²";
  const priceLabel = formatPriceShort(draft);
  const internalCode = draft.internalCode || "BDS-001";
  const statusLabel = draft.status === "DEPOSIT" ? "Đã cọc" : "Đang bán";

  return (
    <aside
      aria-label="Xem trước"
      className={cn(
        "hidden w-[380px] shrink-0 flex-col gap-4 border-l border-border bg-background p-6",
        "md:flex",
      )}
    >
      <p className="font-mono text-[12px] font-semibold tracking-wider text-muted-foreground uppercase">
        Xem trước
      </p>

      <article className="overflow-hidden rounded-xl border border-border bg-card shadow-[var(--shadow-soft)]">
        <PreviewHero
          internalCode={internalCode}
          statusLabel={statusLabel}
          imageUrl={heroImageUrl}
        />
        <div className="flex flex-col gap-1.5 px-4 py-3">
          <h3 className="text-[14px] font-semibold">{title}</h3>
          <p className="font-mono text-[12px] text-muted-foreground">
            {ward} · {floor}
          </p>
          <div className="flex items-center gap-2.5 text-[11px] text-ink-soft">
            <span className="inline-flex items-center gap-1">
              <Square aria-hidden="true" className="size-3" />
              {area}
            </span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1">
              <Bed aria-hidden="true" className="size-3" />
              {beds}
            </span>
          </div>
          <p className="mt-2 border-t border-border pt-2 font-mono text-[16px] font-bold tracking-tight">
            {priceLabel}
          </p>
        </div>
      </article>

      <AiHint draft={draft} />

      <p className="text-center font-mono text-[11px] leading-relaxed text-ink-faint">
        Xem trước cập nhật theo nội dung bạn nhập ở bên trái
      </p>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Subviews
// ---------------------------------------------------------------------------

function PreviewHero({
  internalCode,
  statusLabel,
  imageUrl,
}: {
  internalCode: string;
  statusLabel: string;
  imageUrl: string | undefined;
}) {
  return (
    <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300">
      {imageUrl !== undefined ? (
        <AuthImg
          src={imageUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
      <div className="absolute top-2 left-2 flex gap-1.5">
        <span className="rounded bg-foreground/85 px-1.5 py-0.5 font-mono text-[11px] text-background">
          {internalCode}
        </span>
        <span className="rounded bg-success px-1.5 py-0.5 text-[11px] font-semibold text-background">
          {statusLabel}
        </span>
      </div>
    </div>
  );
}

function AiHint({ draft }: { draft: PropertyDraft }) {
  const tip = buildAiHint(draft);
  return (
    <div className="rounded-lg border border-primary/15 bg-primary/5 px-3 py-2.5">
      <p className="mb-1 inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
        <Sparkles aria-hidden="true" className="size-3" /> Gợi ý AI
      </p>
      <p className="text-[12px] leading-relaxed text-ink-soft">{tip}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPriceShort(draft: PropertyDraft): string {
  if (draft.price == null) {
    return draft.listingType === "RENT" ? "-- tr/th" : "-- tỷ";
  }
  if (draft.listingType === "RENT") {
    return `${(draft.price / 1_000_000).toFixed(0)} tr/th`;
  }
  return `${(draft.price / 1_000_000_000).toFixed(1)} tỷ`;
}

function buildAiHint(draft: PropertyDraft): string {
  if (!draft.buildingName) {
    return "Thêm tên toà nhà và khu vực để bài đăng dễ tìm hơn.";
  }
  if (!draft.description || draft.description.length < 80) {
    return "Mô tả 80-200 ký tự với từ khoá địa danh sẽ giúp tăng lượt xem.";
  }
  if (draft.propertyType === "APARTMENT" && draft.area && draft.area > 90) {
    return "Căn hộ rộng — nhấn mạnh view, ban công và hướng nắng trong tiêu đề.";
  }
  return `Thêm từ khoá "view ${draft.wardCode ? wardLabel(draft.wardCode) : "đẹp"}", "${propertyTypeLabel(draft.propertyType) || "căn hộ"} cao cấp" để tăng tìm kiếm.`;
}
